import React, { useState, useEffect } from "react";
import {
  Button,
  FormControl,
  FormGroup,
  FormLabel,
  Modal,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import EditIcon from "../../../images/edit-icon.svg";
import DeleteIcon from "../../../images/delete-icon.svg";
import PlusIcon from "../../../images/plus.png";
import FileIcon from "../../../images/delete-file.svg";
import FileUploader from "../FileUploader/FileUpload.tsx";
import { giftItemApi } from "./Api.tsx";
import { toast } from "react-toastify";

interface GiftCardItem {
  _id?: string;
  name: string;
  img: any;
  denominations?: string[];
}

interface GiftCardProps {
  onEdit: (item: GiftCardItem) => void;
  onDelete: (name: string) => void;
}

const GiftCard: React.FC<GiftCardProps> = ({ onEdit, onDelete }) => {
  const [giftCardItems, setGiftCardItems] = useState<GiftCardItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);

  // Add Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [selectedItem, setSelectedItem] = useState<GiftCardItem | null>(null);

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<GiftCardItem | null>(null);

  // Fetch gift card items on component mount
  useEffect(() => {
    fetchGiftCardItems();
  }, []);

  const fetchGiftCardItems = async () => {
    try {
      setLoading(true);
      const response = await giftItemApi.getAllGiftItems({ type: 'GiftCard' });
      if (response.status === 'Success') {
        // Convert API data to match component interface
        const apiItems = response.data?.map((item: any) => ({
          _id: item._id,
          name: item.title,
          img: item.imageUrl || PlusIcon,
          denominations: item.denominations?.map(String) || []
        })) || [];
        
        setGiftCardItems(apiItems);
      } else {
        toast.error(response.message || 'Failed to fetch gift card items');
      }
    } catch (error) {
      console.error('Error fetching gift card items:', error);
      toast.error('Failed to fetch gift card items');
    } finally {
      setLoading(false);
    }
  };

  // Modal Handlers
  const handleAddShow = () => setShowAddModal(true);
  const handleAddClose = () => {
    setShowAddModal(false);
    setNewTitle("");
    setUploadedImageUrl("");
    setSelectedImageFile(null);
  };

  const handleEditShow = (item: GiftCardItem) => {
    setSelectedItem(item);
    setEditTitle(item.name);
    setUploadedImageUrl(item.img);
    setShowEditModal(true);
  };

  const handleEditClose = () => {
    setSelectedItem(null);
    setEditTitle("");
    setUploadedImageUrl("");
    setSelectedImageFile(null);
    setShowEditModal(false);
  };

  // Delete Modal Handlers
  const handleDeleteShow = (item: GiftCardItem) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    if (!itemToDelete._id) {
      toast.error('Cannot delete item without ID');
      handleDeleteClose();
      return;
    }

    try {
      setLoading(true);
      
      const response = await giftItemApi.softDeleteGiftItem(itemToDelete._id);
      
      if (response.status === 'Success') {
        toast.success(response.message || 'Gift card deleted successfully');
        await fetchGiftCardItems();
        handleDeleteClose();
      } else {
        toast.error(response.message || 'Failed to delete gift card');
      }
    } catch (error: any) {
      console.error('Error deleting gift card:', error);
      toast.error(error.response?.data?.message || 'Failed to delete gift card');
    } finally {
      setLoading(false);
    }
  };

  // Add Submit
  const handleAddSubmit = async () => {
    if (!newTitle.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!selectedImageFile) {
      toast.error('Image is required');
      return;
    }

    try {
      setLoading(true);
      
      const formData = new FormData();
      formData.append('type', 'GiftCard');
      formData.append('title', newTitle.trim());
      
      // Add the image file
      formData.append('image', selectedImageFile);
      
      const response = await giftItemApi.createGiftItem(formData);
      
      if (response.status === 'Success') {
        toast.success(response.message || 'Gift card created successfully');
        await fetchGiftCardItems();
        handleAddClose();
      } else {
        toast.error(response.message || 'Failed to create gift card');
      }
    } catch (error: any) {
      console.error('Error creating gift card:', error);
      toast.error(error.response?.data?.message || 'Failed to create gift card');
    } finally {
      setLoading(false);
    }
  };

  // Edit Submit
  const handleEditSubmit = async () => {
    if (!selectedItem || !editTitle.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!selectedItem._id) {
      toast.error('Cannot update item without ID');
      return;
    }

    try {
      setLoading(true);
      
      const formData = new FormData();
      formData.append('title', editTitle.trim());
      
      // Add image file if a new one was selected
      if (selectedImageFile) {
        formData.append('image', selectedImageFile);
      }
      
      const response = await giftItemApi.updateGiftItem(selectedItem._id, formData);
      
      if (response.status === 'Success') {
        toast.success(response.message || 'Gift card updated successfully');
        await fetchGiftCardItems();
        handleEditClose();
      } else {
        toast.error(response.message || 'Failed to update gift card');
      }
    } catch (error: any) {
      console.error('Error updating gift card:', error);
      toast.error(error.response?.data?.message || 'Failed to update gift card');
    } finally {
      setLoading(false);
    }
  };


  // Handle file selection from FileUploader component
  const handleFileSelect = (file: File | null) => {
    setSelectedImageFile(file);
    if (file) {
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      setUploadedImageUrl(previewUrl);
    } else {
      setUploadedImageUrl("");
    }
  };

  return (
    <>
      <div className="gift-card-sec row">
        {loading && giftCardItems.length === 0 ? (
          // Show only loader when initially loading
          <div className="col-12">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span className="ms-2">Loading gift cards...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Render existing items */}
            {giftCardItems.map((item, idx) => (
              <div className="default-card col-xl-4 col-lg-6" key={item._id || idx}>
                <div className="default-card-in">
                  <figure>
                    <img 
                      src={item.img} 
                      alt={item.name}
                      onError={(e) => {
                        // Fallback to plus icon if image fails to load
                        e.currentTarget.src = PlusIcon;
                      }}
                    />
                  </figure>
                  <div className="cont">
                    <aside className="name" style={{
                      marginBottom: '8px',
                      textTransform: 'capitalize'
                    }} title={item.name}>
                      <span>{item.name.length > 25 ? item.name.slice(0, 25) + '...' : item.name}</span>
                    </aside>
                    <aside className="options d-flex justify-content-center gap-2">
                      <Button
                        variant="link"
                        className="p-0"
                        onClick={() => handleEditShow(item)}
                        disabled={loading}
                        title="Edit"
                      >
                        <img src={EditIcon} alt="Edit" />
                      </Button>
                      <Button
                        variant="link"
                        className="p-0"
                        onClick={() => handleDeleteShow(item)}
                        disabled={loading}
                        title="Delete"
                      >
                        <img src={DeleteIcon} alt="Delete" />
                      </Button>
                    </aside>
                  </div>
                </div>
              </div>
            ))}

            {/* Add New Card - Always show when not initially loading */}
            <div className="add-block col-xl-4 col-lg-6">
              <div className="add-block-in">
                <div onClick={handleAddShow} className="text-decoration-none" style={{ cursor: 'pointer' }}>
                  <div className="add-block-cont text-center">
                    <div className="table-wrapper">
                      <aside>
                        <figure>
                          <img src={PlusIcon} alt="Add New" />
                        </figure>
                        <h2>Add New</h2>
                      </aside>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Modal */}
      <Modal className="main-modal" show={showAddModal} onHide={handleAddClose} centered>
        <Modal.Header closeButton className="px-0">
          <Modal.Title>Add New Gift Card</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-0 mt-2">
          <FormGroup className="form-group">
            <FormLabel>Title *</FormLabel>
            <FormControl
              type="text"
              placeholder="Enter gift card name"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              disabled={loading}
              maxLength={100}
            />
          </FormGroup>

          <FileUploader onFileSelect={handleFileSelect} />
        </Modal.Body>
        <Modal.Footer className="px-0 border-0">
          <Button
            className="main-bttn"
            variant="primary"
            onClick={handleAddSubmit}
            disabled={!newTitle.trim() || !selectedImageFile || loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Adding...
              </>
            ) : (
              'Add'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal className="main-modal" show={showEditModal} onHide={handleEditClose} centered>
        <Modal.Header closeButton className="px-0">
          <Modal.Title>Edit Gift Card</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-0 mt-2">
          <FormGroup className="form-group">
            <FormLabel>Title *</FormLabel>
            <FormControl
              type="text"
              placeholder="Enter gift card name"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              disabled={loading}
              maxLength={100}
            />
          </FormGroup>

          <FileUploader 
            onFileSelect={handleFileSelect}
            initialImageUrl={selectedItem?.img}
          />
        </Modal.Body>
        <Modal.Footer className="px-0 border-0">
          <Button
            className="main-bttn"
            variant="primary"
            onClick={handleEditSubmit}
            disabled={!editTitle.trim() || loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={handleDeleteClose}
        centered
        className="delete-modal"
      >
        <Modal.Header closeButton style={{ border: 'none' }}>
          <Modal.Title style={{ textTransform: 'capitalize' }}>DELETE CONFIRMATION</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <img src={FileIcon} alt="" />
            </div>
            <div>
              <p className='mw-100' style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                Are you sure you want to delete "{itemToDelete?.name}"?
              </p>
              <p className='mw-100'>This action cannot be undone.</p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 text-left gap-3">
          <Button
            className="border-bttn"
            variant="secondary"
            onClick={handleDeleteClose}
            disabled={loading}
            style={{ width: "30%", margin: "0" }}
          >
            Cancel
          </Button>
          <Button
            className="main-bttn"
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={loading}
            style={{ width: "30%", margin: "0" }}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .delete-modal .btn-close {
          filter: brightness(0) invert(1);
        }
      `}</style>
    </>
  );
};

export default GiftCard;
